import { useFoldersPagination } from '@/hooks/useDomains';
import { useDomainsStore } from '@/store/domains.store';
import { ChevronLeft, ChevronRight, FolderOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Folders pagination component
export const FoldersPagination = () => {
  const { t } = useTranslation();
  const { foldersLimit } = useDomainsStore();
  const {
    currentPage,
    totalPages,
    totalItems,
    hasNext,
    hasPrev,
    goToPage,
    nextPage,
    prevPage,
  } = useFoldersPagination();

  if (totalPages <= 1) return null;

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-center text-sm text-neutral-600 sm:text-left">
        {t('domains.folders.pagination.showing', 'Showing')}{' '}
        {(currentPage - 1) * foldersLimit + 1}-{Math.min(currentPage * foldersLimit, totalItems)}{' '}
        {t('domains.folders.pagination.of', 'of')} {totalItems}{' '}
        {t('domains.folders.pagination.folders.title', 'folders')}
      </p>

      <div className="flex items-center justify-center gap-2">
        <button
          onClick={prevPage}
          disabled={!hasPrev}
          className="rounded-lg border border-neutral-300 p-2 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <div className="hidden items-center gap-2 sm:flex">
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
            if (page > totalPages) return null;

            return (
              <button
                key={page}
                onClick={() => goToPage(page)}
                className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  page === currentPage
                    ? 'bg-primary-600 text-white'
                    : 'text-neutral-700 hover:bg-neutral-50'
                }`}
              >
                {page}
              </button>
            );
          })}
        </div>

        {/* Mobile pagination info */}
        <div className="px-3 py-2 text-sm font-medium text-neutral-700 sm:hidden">
          {currentPage} / {totalPages}
        </div>

        <button
          onClick={nextPage}
          disabled={!hasNext}
          className="rounded-lg border border-neutral-300 p-2 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

// Empty state for folders
export const FoldersEmptyState = () => {
  const { t } = useTranslation();

  return (
    <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-8 text-center sm:p-12">
      <div className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-neutral-100 sm:h-12 sm:w-12">
        <FolderOpen className="h-5 w-5 text-neutral-400 sm:h-6 sm:w-6" />
      </div>
      <h3 className="mb-2 text-lg font-semibold text-neutral-900">
        {t('domains.folders.empty.title', 'No folders found')}
      </h3>
      <p className="mx-auto max-w-md text-sm text-neutral-600 sm:text-base">
        {t(
          'domains.folders.empty.description',
          'This domain does not contain any folders yet.'
        )}
      </p>
    </div>
  );
};

// Empty state for global folders
export const GlobalFoldersEmptyState = () => {
  const { t } = useTranslation();

  return (
    <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-8 text-center sm:p-12">
      <div className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-neutral-100 sm:h-12 sm:w-12">
        <FolderOpen className="h-5 w-5 text-neutral-400 sm:h-6 sm:w-6" />
      </div>
      <h3 className="mb-2 text-lg font-semibold text-neutral-900">
        {t('domains.folders.global.empty.title', 'No folders found')}
      </h3>
      <p className="mx-auto max-w-md text-sm text-neutral-600 sm:text-base">
        {t(
          'domains.folders.global.empty.description',
          'No folders match your current search criteria. Try adjusting your filters or search terms.'
        )}
      </p>
    </div>
  );
};
